import React, { useState, useCallback } from 'react';
import { Shield, Lock, Star, ChevronRight, Zap } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import Login from './Auth/Login';
import Register from './Auth/Register';

// Component to show when user doesn't have required subscription
const SubscriptionRequired = ({ requiredPlan, onUpgrade }) => {
  const planFeatures = {
    premium: [
      'Unlimited deal access',
      'Advanced filtering & sorting',
      'Price history & analytics',
      'Custom deal alerts',
      'Mobile app access',
      'Priority support'
    ],
    pro: [
      'Everything in Premium',
      'API access',
      'Bulk deal management',
      'White-label options',
      'Custom integrations',
      'Dedicated account manager'
    ]
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8 text-center">
        <div className="mb-6">
          <div className="w-16 h-16 bg-gradient-to-r from-yellow-400 to-yellow-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <Star className="text-white" size={32} />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {requiredPlan === 'pro' ? 'Pro' : 'Premium'} Required
          </h2>
          <p className="text-gray-600">
            Upgrade your account to access this premium feature
          </p>
        </div>

        <div className="mb-8">
          <div className="text-left space-y-3">
            <p className="text-sm font-medium text-gray-700 mb-3">
              {requiredPlan === 'pro' ? 'Pro' : 'Premium'} features include:
            </p>
            {planFeatures[requiredPlan]?.map((feature, index) => (
              <div key={index} className="flex items-start space-x-3">
                <Zap className="text-green-500 flex-shrink-0 mt-0.5" size={16} />
                <span className="text-sm text-gray-700">{feature}</span>
              </div>
            ))}
          </div>
        </div>

        <button
          onClick={onUpgrade}
          className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white py-3 px-4 rounded-xl font-bold text-lg hover:shadow-lg transition-all duration-300 transform hover:scale-105 flex items-center justify-center space-x-2"
        >
          <span>Upgrade to {requiredPlan === 'pro' ? 'Pro' : 'Premium'}</span>
          <ChevronRight size={20} />
        </button>

        <p className="text-xs text-gray-500 mt-4">
          30-day money-back guarantee • Cancel anytime
        </p>
      </div>
    </div>
  );
};

// Component to show when user is not authenticated
const AuthRequired = ({ onLogin, onRegister }) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8 text-center">
        <div className="mb-6">
          <div className="w-16 h-16 bg-gradient-to-r from-green-600 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="text-white" size={32} />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Sign In Required
          </h2>
          <p className="text-gray-600">
            Please sign in to your account to access premium cannabis deals
          </p>
        </div>

        <div className="space-y-4">
          <button
            onClick={onLogin}
            className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white py-3 px-4 rounded-xl font-bold text-lg hover:shadow-lg transition-all duration-300 transform hover:scale-105"
          >
            Sign In
          </button>
          
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">Don't have an account?</span>
            </div>
          </div>
          
          <button
            onClick={onRegister}
            className="w-full border-2 border-green-600 text-green-600 py-3 px-4 rounded-xl font-bold text-lg hover:bg-green-50 transition-all duration-300"
          >
            Create Free Account
          </button>
        </div>

        <div className="mt-8 p-4 bg-green-50 rounded-lg">
          <div className="flex items-center space-x-2 mb-2">
            <Shield className="text-green-600" size={16} />
            <span className="text-sm font-medium text-green-700">Secure & Private</span>
          </div>
          <p className="text-xs text-green-600">
            Your data is encrypted and we never share your information with third parties.
          </p>
        </div>
      </div>
    </div>
  );
};

// Loading component
const LoadingScreen = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 flex items-center justify-center">
      <div className="text-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mb-4"></div>
        <p className="text-gray-600 font-medium">Loading...</p>
      </div>
    </div>
  );
};

// Main ProtectedRoute component
const ProtectedRoute = ({ 
  children, 
  requireAuth = true, 
  requiredSubscription = null,
  requiredFeature = null,
  fallback = null 
}) => {
  const { 
    isAuthenticated, 
    isLoading, 
    hasFeatureAccess 
  } = useAuth();
  
  const [showLogin, setShowLogin] = useState(false);
  const [showRegister, setShowRegister] = useState(false);

  // Handle authentication modal controls
  const handleShowLogin = useCallback(() => {
    setShowLogin(true);
    setShowRegister(false);
  }, []);

  const handleShowRegister = useCallback(() => {
    setShowRegister(true);
    setShowLogin(false);
  }, []);

  const handleCloseAuth = useCallback(() => {
    setShowLogin(false);
    setShowRegister(false);
  }, []);

  const handleSwitchToRegister = useCallback(() => {
    setShowLogin(false);
    setShowRegister(true);
  }, []);

  const handleSwitchToLogin = useCallback(() => {
    setShowRegister(false);
    setShowLogin(true);
  }, []);

  // Handle subscription upgrade
  const handleUpgrade = useCallback(() => {
    // This would typically redirect to a payment processor like Stripe
    // For now, we'll just show the register modal with premium plan
    setShowRegister(true);
    setShowLogin(false);
  }, []);

  // Show loading screen while checking authentication
  if (isLoading) {
    return <LoadingScreen />;
  }

  // Check if authentication is required
  if (requireAuth && !isAuthenticated) {
    return (
      <>
        <AuthRequired 
          onLogin={handleShowLogin}
          onRegister={handleShowRegister}
        />
        
        {/* Auth Modals */}
        {showLogin && (
          <Login 
            onClose={handleCloseAuth}
            onSwitchToRegister={handleSwitchToRegister}
          />
        )}
        
        {showRegister && (
          <Register 
            onClose={handleCloseAuth}
            onSwitchToLogin={handleSwitchToLogin}
            initialPlan="premium"
          />
        )}
      </>
    );
  }

  // Check feature access if specified
  if (requiredFeature && !hasFeatureAccess(requiredFeature)) {
    // Determine required plan based on feature
    let requiredPlan = 'premium';
    const proFeatures = ['api_access', 'bulk_management', 'white_label', 'custom_integrations', 'analytics_dashboard'];
    if (proFeatures.includes(requiredFeature)) {
      requiredPlan = 'pro';
    }

    return (
      <>
        <SubscriptionRequired 
          requiredPlan={requiredPlan}
          onUpgrade={handleUpgrade}
        />
        
        {/* Auth Modals */}
        {showRegister && (
          <Register 
            onClose={handleCloseAuth}
            onSwitchToLogin={handleSwitchToLogin}
            initialPlan={requiredPlan}
          />
        )}
      </>
    );
  }

  // SIMPLIFIED: All users are premium with active status after payment
  // No complex subscription checking needed
  
  // REMOVED: No subscription status checking needed
  // If user is authenticated, they already paid and have access

  // All checks passed, render the protected content
  return (
    <>
      {children}
      
      {/* Auth Modals (in case they're triggered from within the protected content) */}
      {showLogin && (
        <Login 
          onClose={handleCloseAuth}
          onSwitchToRegister={handleSwitchToRegister}
        />
      )}
      
      {showRegister && (
        <Register 
          onClose={handleCloseAuth}
          onSwitchToLogin={handleSwitchToLogin}
        />
      )}
    </>
  );
};

export default ProtectedRoute;
